import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, Copy } from 'lucide-react';

interface ViewManifestDialogProps {
  appName: string;
  manifest: unknown;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewManifestDialog({
  appName,
  manifest,
  open,
  onOpenChange,
}: ViewManifestDialogProps) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(manifest, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable; silently ignore
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{appName} manifest</DialogTitle>
          <DialogDescription>
            Read-only view of the registered app manifest.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <pre className="bg-muted rounded-md p-4 text-xs font-mono overflow-auto max-h-[60vh]">
            {json}
          </pre>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="absolute right-2 top-2"
          >
            {copied ? (
              <>
                <Check className="mr-1 h-3 w-3" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-1 h-3 w-3" />
                Copy
              </>
            )}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
